/**
 * Native replacement for lodash `omit`. Returns a new object with the given
 * `keys` removed. Keys may be passed variadically or as arrays (matching
 * lodash, which flattens its `paths` argument). A nullish `obj` yields an empty
 * object (matching lodash). Operates on own enumerable keys only and does not
 * support deep key paths (which we do not use).
 */
export default function omit<T extends object, K extends keyof T>(
    obj: T | null | undefined,
    ...keys: (K | readonly K[])[]
): Omit<T, K> {
    const result = {} as Record<string, unknown>;
    if (obj == null) return result as Omit<T, K>;
    // `Object.keys` yields string keys, so coerce the omit keys to strings too
    // (matching lodash, which treats a numeric key arg like `1` as `'1'`).
    const toOmit = new Set(keys.flat().map(key => String(key)));
    Object.keys(obj).forEach(key => {
        if (!toOmit.has(key)) {
            result[key] = (obj as Record<string, unknown>)[key];
        }
    });
    return result as Omit<T, K>;
}
